// src/schedule/NeededMenu.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Popup "Needed":
 * - „na chama” przypięty do rogu kontenera menu (portalRoot: div.relative obok ⚙️)
 * - auto drop-up, gdy brakuje miejsca w dół
 * - na mobile (<=640px) działa jako dolny sheet (nie znika poza viewport, własny scroll)
 * - zamyka się po kliknięciu poza i po ESC
 */
export default function NeededMenu({
    anchorEl,                 // tylko na potrzeby zamykania kliknięciem w anchor
    portalRoot = null,
    event,                    // { staff, needed, ... }
    date,
    eventIndex,
    updateNeededForEvent,     // (date, eventRef|index, needed, calendarId?, save?) => void
    onRegisterPanelRef,       // (el) => void — rodzic rejestruje ref do panelu
    closeMenu,                // () => void
}) {
    const ref = useRef(null);
    const [anchorRect, setAnchorRect] = useState(null);

    // rejestracja panelu w rodzicu
    useEffect(() => {
        onRegisterPanelRef?.(ref.current);
        return () => onRegisterPanelRef?.(null);
    }, [onRegisterPanelRef]);

    const staffLen = useMemo(
        () => (Array.isArray(event?.staff) ? event.staff.length : 0),
        [event?.staff]
    );
    const minNeeded = Math.max(1, staffLen);

    const [value, setValue] = useState(() => {
        const raw = Number(event?.needed);
        return Number.isFinite(raw) ? Math.max(minNeeded, raw) : minNeeded;
    });

    useEffect(() => {
        const raw = Number(event?.needed);
        const initial = Number.isFinite(raw) ? Math.max(minNeeded, raw) : minNeeded;
        setValue(initial);
    }, [event?.needed, minNeeded]);

    const clamp = (v) => Math.max(minNeeded, Math.floor(Number(v) || 0));
    const commit = (next) => {
        const v = clamp(next);
        setValue(v);
        const calId = event?.calendarId;
        const eventRef = event?.gId
            ? { gId: event.gId, event: event.event, calendarId: calId }
            : { event: event?.event, calendarId: calId };
        updateNeededForEvent?.(date, eventRef, v, calId, true);
    };

    // === TRYB WYŚWIETLANIA ===
    // 'dropdown' (desktop, przypięte do rogu kontenera menu)
    // 'sheet'    (mobile, stały panel u dołu ekranu)
    const [mode, setMode] = useState('dropdown');
    const [placement, setPlacement] = useState('down'); // 'down' | 'up'

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 640px)');
        const apply = () => setMode(mq.matches ? 'sheet' : 'dropdown');
        apply();
        mq.addEventListener('change', apply);
        return () => mq.removeEventListener('change', apply);
    }, []);

    // śledzenie pozycji anchor'a, także przy scrollu/zoomie
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const source = anchorEl || portalRoot;
        if (!source || typeof source.getBoundingClientRect !== 'function') {
            setAnchorRect(null);
            return;
        }

        const update = () => {
            try {
                const rect = source.getBoundingClientRect();
                setAnchorRect(rect);
            } catch { }
        };

        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [anchorEl, portalRoot]);

    // Pomiar wysokości panelu względem okna — decyduje czy pokazujemy w górę, czy w dół
    useEffect(() => {
        if (mode !== 'dropdown' || typeof window === 'undefined') return;
        const el = ref.current;
        if (!el || !anchorRect) return;

        const id = window.requestAnimationFrame(() => {
            const panelHeight = el.offsetHeight || 0;
            const spaceBelow = window.innerHeight - (anchorRect.bottom + 12);
            setPlacement(spaceBelow < panelHeight ? 'up' : 'down');
        });

        return () => window.cancelAnimationFrame(id);
    }, [mode, anchorRect]);

    // Zamknięcie po kliknięciu poza + ESC
    useEffect(() => {
        const onDown = (e) => {
            const m = ref.current;
            const t = e.target;
            if (!m) return;
            if (m.contains(t)) return;
            if (anchorEl && anchorEl.contains && anchorEl.contains(t)) return;
            closeMenu?.();
        };
        const onKey = (e) => { if (e.key === "Escape") closeMenu?.(); };
        document.addEventListener("mousedown", onDown, true);
        document.addEventListener("keydown", onKey, true);
        return () => {
            document.removeEventListener("mousedown", onDown, true);
            document.removeEventListener("keydown", onKey, true);
        };
    }, [anchorEl, closeMenu]);

    // DESKTOP – dropdown przy rogu kontenera (bez liczenia, tylko kierunek)
    const OVERLAY_Z = 2147483648;
    const GAP = 8;

    const dropdownDownStyle = { position: 'fixed', top: 16, right: 16, zIndex: OVERLAY_Z };
    const dropdownUpStyle = { position: 'fixed', bottom: 16, right: 16, zIndex: OVERLAY_Z };

    const anchoredStyle = anchorRect
        ? (placement === 'up'
            ? {
                position: 'fixed',
                left: anchorRect.right,
                top: anchorRect.top - GAP,
                transform: 'translate(-100%, -100%)',
                zIndex: OVERLAY_Z,
            }
            : {
                position: 'fixed',
                left: anchorRect.right,
                top: anchorRect.bottom + GAP,
                transform: 'translateX(-100%)',
                zIndex: OVERLAY_Z,
            })
        : null;

    // MOBILE – dolny „sheet"
    const sheetStyle = {
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)', // Zwiększone z 8px na 80px, żeby nie było za nisko
        width: 'min(480px, calc(100vw - 16px))',
        maxHeight: 'min(70vh, calc(100vh - 24px))',
        zIndex: 2147483648, // Wyższy niż powiększona karta
    };

    const wrapperStyle =
        mode === 'sheet'
            ? sheetStyle
            : (anchoredStyle || (placement === 'up' ? dropdownUpStyle : dropdownDownStyle));

    const body = (
        <div
            ref={ref}
            role="dialog"
            aria-label="Ustaw liczbę potrzebnych osób"
            style={wrapperStyle}
            className="bg-gray-800 text-white border border-gray-700 rounded-lg shadow-2xl px-3 py-2.5 w-52 sm:w-56 pointer-events-auto text-sm"
            data-testid="needed-panel"
        >
            {/* przycięcie zawartości: własny scroll */}
            <div className={`${mode === 'sheet' ? 'max-h-[60vh]' : 'max-h-72'} overflow-y-auto pr-1`}>
                <div className="text-sm font-semibold mb-2">Potrzebnych osób</div>
                <div className="flex items-center gap-1.5">
                    <button
                        onClick={() => commit(value - 1)}
                        className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-base"
                        title="Zmniejsz"
                    >−</button>
                    <input
                        type="number"
                        min={minNeeded}
                        value={value}
                        onChange={(e) => commit(e.target.value)}
                        className="w-16 text-center px-2 py-1 rounded bg-gray-900 border border-gray-700"
                    />
                    <button
                        onClick={() => commit(value + 1)}
                        className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-base"
                        title="Zwiększ"
                    >+</button>
                </div>

                <div className="mt-3 text-xs text-gray-400">
                    Min: {minNeeded} (już przypisani)
                </div>
            </div>

            <div className="mt-3 flex justify-end">
                <button
                    onClick={() => closeMenu?.()}
                    className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600"
                >Zamknij</button>
            </div>
        </div>
    );

    const globalPortal = typeof document !== 'undefined' ? document.body : null;
    const targetPortal = globalPortal || portalRoot;
    return targetPortal ? createPortal(body, targetPortal) : body;
}
